import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { Check, X } from 'lucide-react-native';
import { useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { PUBLIC_PRICING_VISIBLE } from '../../config/pricingVisibility';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { formatPriceParts, sharedPriceFontSize } from '../../features/pricing/formatPrice';
import {
  GLOBAL_MARKET_SELECTION,
  useCountryPreference,
} from '../../features/pricing/useCountryPreference';
import { type PublicPlanPricing, usePublicPricing } from '../../features/pricing/usePricing';
import { trackEvent } from '../../lib/analytics';
import { createCheckoutIntent, saveCheckoutIntent } from '../../lib/billing/checkoutIntent';
import { normalizeCountryCode } from '../../lib/geo/localeCountry';
import { useInferredLocale } from '../../lib/geo/useInferredLocale';
import { useMixpanel } from '../../lib/mixpanel';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { AnimatedSection, FadeIn } from '../animations';
import { Badge } from '../primitives/Badge';
import { Button } from '../primitives/Button';
import { Container } from '../primitives/Container';
import { Text } from '../primitives/Text';
import { CountrySelector } from './CountrySelector';
import {
  GLASS_GRADIENT_END,
  GLASS_GRADIENT_START,
  GLASS_GRADIENT_STRONG,
  GLASS_GRADIENT_SUBTLE,
  GlowBloom,
} from './landingStyleKit';

type PlanKey = 'starter' | 'growth' | 'professional' | 'business' | 'enterprise';

const PLAN_KEYS: PlanKey[] = ['starter', 'growth', 'professional', 'business', 'enterprise'];

const HIGHLIGHTED_PLAN: PlanKey = 'growth';

const PRICE_BASE_FONT_SIZE = Platform.select({ web: 40, default: 32 }) ?? 40;

function formatPrice(amountInMinor: number, currencyCode: 'COP' | 'USD'): string {
  const { amount } = formatPriceParts(amountInMinor, currencyCode);
  return `${currencyCode} ${amount}`;
}

function isDisplayablePrice(
  amountInMinor: number | null | undefined,
  currencyCode: 'COP' | 'USD' | undefined,
): amountInMinor is number {
  return (
    typeof amountInMinor === 'number' &&
    Number.isSafeInteger(amountInMinor) &&
    amountInMinor > 0 &&
    !!currencyCode &&
    (currencyCode !== 'COP' || amountInMinor % 100 === 0)
  );
}

// Only ships what exists. `whatsapp`, `sms`, and `workspaces` are deliberately
// absent: they are not built yet, and advertising them on a page with a
// checkout button sells something we cannot deliver. Their translation strings
// are kept so each row is a one-line restore once the feature actually lands.
const FEATURE_KEYS = [
  'credits',
  'aiAgents',
  'aiLeadGeneration',
  'aiVoiceAgent',
  'aiLeadScoring',
  'analytics',
  'teamMembers',
  'support',
  'onboarding',
] as const;

const BillingToggle = ({
  isYearly,
  onToggle,
  monthlyLabel,
  yearlyLabel,
  discountLabel,
}: {
  isYearly: boolean;
  onToggle: () => void;
  monthlyLabel: string;
  yearlyLabel: string;
  discountLabel: string;
}) => {
  return (
    <View style={toggleStyles.wrapper}>
      <View style={toggleStyles.container}>
        <Pressable
          testID="pricing_cycle_monthly"
          onPress={isYearly ? onToggle : undefined}
          style={[toggleStyles.option, !isYearly && toggleStyles.activeOption]}
          accessibilityRole="button"
          accessibilityState={{ selected: !isYearly }}
        >
          <Text
            variant="bodySmall"
            style={[toggleStyles.label, !isYearly && toggleStyles.activeLabel]}
          >
            {monthlyLabel}
          </Text>
        </Pressable>
        <Pressable
          testID="pricing_cycle_yearly"
          onPress={isYearly ? undefined : onToggle}
          style={[toggleStyles.option, isYearly && toggleStyles.activeOption]}
          accessibilityRole="button"
          accessibilityState={{ selected: isYearly }}
        >
          <Text
            variant="bodySmall"
            style={[toggleStyles.label, isYearly && toggleStyles.activeLabel]}
          >
            {yearlyLabel}
          </Text>
        </Pressable>
      </View>
      {isYearly && (
        <Badge color="green" variant="subtle" size="sm">
          {discountLabel}
        </Badge>
      )}
    </View>
  );
};

const FeatureValue = ({ value }: { value: boolean | string }) => {
  const { t } = useTranslation();
  if (typeof value === 'boolean') {
    const label = value ? t('landing.pricing.included') : t('landing.pricing.notIncluded');
    return value ? (
      <View
        style={featureStyles.checkCircle}
        accessible
        accessibilityRole="text"
        accessibilityLabel={label}
      >
        <Check size={12} color={colors.status.success.main} strokeWidth={3} />
      </View>
    ) : (
      <View accessible accessibilityRole="text" accessibilityLabel={label}>
        <X size={16} color={colors.text.disabled} strokeWidth={2} />
      </View>
    );
  }
  return (
    <Text variant="bodySmall" style={featureStyles.valueText}>
      {value}
    </Text>
  );
};

const PlanCard = ({
  destination,
  planKey,
  isHighlighted,
  isYearly,
  index,
  cardWidth,
  pricing,
  currencyCode,
  pricingUnavailable,
  priceSize,
}: {
  planKey: PlanKey;
  isHighlighted: boolean;
  isYearly: boolean;
  index: number;
  cardWidth: number | undefined;
  pricing: PublicPlanPricing | undefined;
  destination: '/login' | '/waitlist';
  currencyCode: 'COP' | 'USD' | undefined;
  pricingUnavailable: boolean;
  priceSize: number;
}) => {
  const { t, getTranslation } = useTranslation();
  const router = useRouter();
  const mp = useMixpanel();
  const { isAuthenticated } = useAuth();

  const isCustomPrice = pricing?.contactSales ?? planKey === 'enterprise';
  const amountInMinor = isYearly
    ? pricing?.annualMonthlyEquivalentInMinor
    : pricing?.monthlyAmountInMinor;
  const hasDisplayablePrice = isDisplayablePrice(amountInMinor, currencyCode);
  const hasInvalidPrice = amountInMinor != null && !hasDisplayablePrice;
  const formattedPrice =
    hasDisplayablePrice && currencyCode
      ? formatPrice(amountInMinor, currencyCode)
      : t(
          pricingUnavailable || hasInvalidPrice
            ? 'landing.pricing.priceUnavailable'
            : 'landing.pricing.priceLoading',
        );
  const price = isCustomPrice ? `${t('landing.pricing.from')} ${formattedPrice}` : formattedPrice;
  // Rendered as two nodes so the code can sit at a smaller size than the number.
  // A single Intl currency string cannot be styled in two sizes, and at one size
  // a COP amount overflows the card.
  const priceParts =
    hasDisplayablePrice && currencyCode ? formatPriceParts(amountInMinor, currencyCode) : null;
  const annualTotal =
    isYearly && pricing?.annualAmountInMinor != null && currencyCode
      ? formatPrice(pricing.annualAmountInMinor, currencyCode)
      : null;
  const startCheckout = async () => {
    if (planKey === 'enterprise' || !hasDisplayablePrice) return;
    const cycle = isYearly ? 'yearly' : 'monthly';
    trackEvent(mp, 'landing.checkout_started', {
      source: 'pricing_plan',
      plan: planKey,
      billing_period: cycle,
    });
    const intent = createCheckoutIntent(planKey, cycle);
    await saveCheckoutIntent(intent);
    router.push(
      isAuthenticated ? { pathname: '/settings', params: { section: 'billing' } } : '/login',
    );
  };

  return (
    <FadeIn delay={200 + index * 120} translateY={20} style={cardStyles.fadeWrapper}>
      <View
        style={[
          cardStyles.card,
          cardWidth ? { width: cardWidth } : undefined,
          { borderColor: isHighlighted ? colors.primary.borderActive : colors.border.muted },
          isHighlighted && cardStyles.highlightedCard,
        ]}
      >
        <LinearGradient
          colors={isHighlighted ? GLASS_GRADIENT_STRONG : GLASS_GRADIENT_SUBTLE}
          start={GLASS_GRADIENT_START}
          end={GLASS_GRADIENT_END}
          style={StyleSheet.absoluteFill}
        />
        {isHighlighted && <GlowBloom size={300} style={cardStyles.cardGlow} />}

        <View style={cardStyles.cardBody}>
          <View style={cardStyles.topSection}>
            <View style={cardStyles.badgeSlot}>
              {isHighlighted && (
                <Badge color="cyan" variant="filled" size="sm">
                  {t('landing.pricing.mostPopular')}
                </Badge>
              )}
            </View>

            <Text variant="h4" aria-level={3} style={cardStyles.planName}>
              {t(`landing.pricing.plans.${planKey}.name`)}
            </Text>

            {/* The currency code sits above rather than beside the number: on a
                243px card it would otherwise consume a quarter of the width and
                force COP amounts down to 20px next to USD's 36px. */}
            <View style={cardStyles.priceCaptionSlot}>
              {PUBLIC_PRICING_VISIBLE && priceParts && (
                <Text
                  variant="caption"
                  color={colors.text.tertiary}
                  style={cardStyles.priceCurrency}
                >
                  {isCustomPrice
                    ? `${t('landing.pricing.from')} ${priceParts.currencyCode}`
                    : priceParts.currencyCode}
                </Text>
              )}
            </View>
            {!PUBLIC_PRICING_VISIBLE && (
              <View style={cardStyles.priceRow}>
                <Text
                  testID={`pricing_coming_soon_${planKey}`}
                  variant="h4"
                  color={colors.text.secondary}
                >
                  {t('landing.pricing.comingSoon')}
                </Text>
              </View>
            )}
            {PUBLIC_PRICING_VISIBLE && (
              <View style={cardStyles.priceRow}>
                <Text
                  testID={`pricing_price_${planKey}`}
                  accessibilityLabel={price}
                  variant="metric"
                  numberOfLines={1}
                  accessibilityLiveRegion="polite"
                  style={[
                    cardStyles.price,
                    priceParts ? { fontSize: priceSize } : cardStyles.priceFallback,
                  ]}
                >
                  {priceParts ? priceParts.amount : price}
                </Text>
                {!isCustomPrice && (
                  <Text variant="bodySmall" color={colors.text.tertiary}>
                    {t('landing.pricing.perMonth')}
                  </Text>
                )}
              </View>
            )}

            <View style={cardStyles.billedYearlySlot}>
              {PUBLIC_PRICING_VISIBLE && annualTotal && !isCustomPrice && (
                <Text
                  testID={`pricing_annual_total_${planKey}`}
                  variant="caption"
                  color={colors.text.tertiary}
                >
                  {`${t('landing.pricing.billedYearly')} ${annualTotal}`}
                </Text>
              )}
            </View>

            <Text variant="bodySmall" color={colors.text.secondary} style={cardStyles.description}>
              {t(`landing.pricing.plans.${planKey}.description`)}
            </Text>
          </View>

          {PUBLIC_PRICING_VISIBLE && (
            <Button
              testID={`pricing_checkout_${planKey}`}
              variant={isHighlighted ? 'primary' : 'secondary'}
              size="lg"
              pill
              fullWidth
              disabled={!isCustomPrice && !hasDisplayablePrice}
              accessibilityState={{ disabled: !isCustomPrice && !hasDisplayablePrice }}
              style={cardStyles.ctaButton}
              onPress={
                isCustomPrice
                  ? () => {
                      const billingPeriod = isYearly ? 'yearly' : 'monthly';
                      trackEvent(mp, 'landing.login_cta_clicked', {
                        source: 'pricing_plan',
                        plan: planKey,
                        billing_period: billingPeriod,
                      });
                      router.push(destination);
                    }
                  : startCheckout
              }
            >
              {pricingUnavailable && !isCustomPrice
                ? t('landing.pricing.checkoutUnavailableCta')
                : t(`landing.pricing.plans.${planKey}.cta`)}
            </Button>
          )}

          <View style={cardStyles.divider} />

          {FEATURE_KEYS.map((featureKey) => {
            const value = getTranslation<boolean | string>(
              `landing.pricing.values.${planKey}.${featureKey}`,
            );
            return (
              <View key={featureKey} style={cardStyles.featureRow}>
                <Text
                  variant="bodySmall"
                  color={colors.text.secondary}
                  style={cardStyles.featureLabel}
                >
                  {t(`landing.pricing.features.${featureKey}`)}
                </Text>
                <FeatureValue value={value ?? false} />
              </View>
            );
          })}
        </View>
      </View>
    </FadeIn>
  );
};

/**
 * Where the signup CTAs lead. Defaults to signup so this stays a pure
 * presentational component: the landing screens own the capacity lookup, and an
 * unanswered gate must never render a waitlist it has not confirmed.
 */
export interface AccessGateCta {
  destination?: '/login' | '/waitlist';
}

export const PricingSection = ({ destination = '/login' }: AccessGateCta = {}) => {
  const { t } = useTranslation();
  const mp = useMixpanel();
  const [isYearly, setIsYearly] = useState(false);
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const { country } = useInferredLocale();
  const { selection, select } = useCountryPreference();
  const {
    data: pricing,
    isLoading: isPricingLoading,
    error: pricingError,
  } = usePublicPricing(normalizeCountryCode(country), selection, {
    // Nothing on screen uses these amounts while prices are withdrawn, and
    // shipping them to every visitor anyway would defeat the point.
    enabled: PUBLIC_PRICING_VISIBLE,
  });

  // What is actually pricing the page right now, straight from the response —
  // detection and an explicit choice both land here, so the selector always
  // reflects the currency on screen rather than a second guess at it.
  const activeMarket = pricing
    ? pricing.market === 'colombia'
      ? 'CO'
      : GLOBAL_MARKET_SELECTION
    : null;

  const handleSelectMarket = (value: string) => {
    trackEvent(mp, 'landing.pricing_market_selected', {
      market: value,
      previous_market: activeMarket,
    });
    select(value);
  };

  // One size for the whole row, driven by the longest amount in it — a price
  // table is a comparison surface, so ragged type sizes undercut it.
  const displayedAmounts = (pricing?.plans ?? [])
    .map((plan) => (isYearly ? plan.annualMonthlyEquivalentInMinor : plan.monthlyAmountInMinor))
    .filter((amount): amount is number => typeof amount === 'number' && amount > 0)
    .map((amount) => formatPriceParts(amount, pricing?.currencyCode ?? 'USD').amount);
  const priceSize = sharedPriceFontSize(displayedAmounts, PRICE_BASE_FONT_SIZE);

  const cardWidth = isDesktop ? undefined : 280;

  return (
    <View style={sectionStyles.section}>
      <Container maxWidth="xl">
        <AnimatedSection delay={0}>
          <View style={sectionStyles.header}>
            <View style={sectionStyles.badge}>
              <Text variant="overline" color={colors.primary[400]}>
                {t('landing.pricing.badge')}
              </Text>
            </View>
            <Text variant="h2" style={sectionStyles.title}>
              {t('landing.pricing.title')}
            </Text>
            <Text variant="bodyLarge" color={colors.text.secondary} style={sectionStyles.subtitle}>
              {/* The standing subtitle invites the reader to lock in a founding
                  price. With no amounts and no checkout on the page that is an
                  invitation to nothing, so it is swapped rather than left to
                  contradict the cards below it (FLOSEAI-673). */}
              {t(
                PUBLIC_PRICING_VISIBLE
                  ? 'landing.pricing.subtitle'
                  : 'landing.pricing.subtitleComingSoon',
              )}
            </Text>
          </View>
        </AnimatedSection>

        <FadeIn delay={150} translateY={10}>
          <View style={sectionStyles.toggleContainer}>
            {PUBLIC_PRICING_VISIBLE && (
              <BillingToggle
                isYearly={isYearly}
                onToggle={() => {
                  const next = !isYearly;
                  trackEvent(mp, 'landing.pricing_billing_toggled', {
                    billing_period: next ? 'yearly' : 'monthly',
                  });
                  setIsYearly(next);
                }}
                monthlyLabel={t('landing.pricing.monthly')}
                yearlyLabel={t('landing.pricing.yearly')}
                discountLabel={t('landing.pricing.yearlyDiscount')}
              />
            )}
            <Text
              variant="caption"
              color={colors.text.tertiary}
              style={sectionStyles.creditsDisclaimer}
            >
              {t('landing.pricing.creditsDisclaimer')}
            </Text>
            {PUBLIC_PRICING_VISIBLE && (
              <Text
                testID="pricing_currency_note"
                variant="caption"
                color={colors.text.tertiary}
                style={sectionStyles.creditsDisclaimer}
              >
                {`${t('landing.pricing.currencyNote')} ${pricing?.currencyCode ?? ''}`}
              </Text>
            )}
            {PUBLIC_PRICING_VISIBLE && (
              <CountrySelector activeCountry={activeMarket} onSelect={handleSelectMarket} />
            )}
          </View>
        </FadeIn>

        {isDesktop ? (
          <View style={sectionStyles.grid}>
            {PLAN_KEYS.map((key, index) => (
              <PlanCard
                key={key}
                destination={destination}
                planKey={key}
                isHighlighted={key === HIGHLIGHTED_PLAN}
                isYearly={isYearly}
                index={index}
                cardWidth={cardWidth}
                pricing={pricing?.plans.find((plan) => plan.plan === key)}
                currencyCode={pricing?.currencyCode}
                pricingUnavailable={!!pricingError && !isPricingLoading}
                priceSize={priceSize}
              />
            ))}
          </View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={sectionStyles.scrollContainer}
          >
            {PLAN_KEYS.map((key, index) => (
              <PlanCard
                key={key}
                destination={destination}
                planKey={key}
                isHighlighted={key === HIGHLIGHTED_PLAN}
                isYearly={isYearly}
                index={index}
                cardWidth={280}
                pricing={pricing?.plans.find((plan) => plan.plan === key)}
                currencyCode={pricing?.currencyCode}
                pricingUnavailable={!!pricingError && !isPricingLoading}
                priceSize={priceSize}
              />
            ))}
          </ScrollView>
        )}
      </Container>
    </View>
  );
};

const toggleStyles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  container: {
    flexDirection: 'row',
    gap: spacing.xs,
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    padding: spacing.xs,
  },
  option: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeOption: {
    backgroundColor: colors.primary[500],
  },
  label: {
    fontWeight: '500',
    color: colors.text.secondary,
  },
  activeLabel: {
    color: colors.text.inverse,
    fontWeight: '600',
  },
});

const featureStyles = StyleSheet.create({
  checkCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.status.success.bg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  valueText: {
    color: colors.text.primary,
    fontWeight: '500',
    fontSize: 12,
    flexShrink: 0,
  },
});

const cardStyles = StyleSheet.create({
  fadeWrapper: {
    flex: 1,
    minWidth: 0,
  },
  card: {
    flex: 1,
    minWidth: 0,
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    overflow: 'hidden',
    backgroundColor: colors.surface.row,
  },
  highlightedCard: {
    borderWidth: 2,
  },
  cardGlow: {
    top: -60,
    alignSelf: 'center',
  },
  cardBody: {
    flex: 1,
    padding: Platform.select({ web: 24, default: 18 }),
    flexDirection: 'column',
  },
  topSection: {
    // Intentionally NOT flex:1 — a growing header couples the CTA's vertical
    // position to the (variable-height) feature list below it, which misaligns
    // the "Start Checkout" buttons across cards. Uniform header height (fixed
    // slots + `description` minHeight) keeps every button on one baseline.
    flexShrink: 0,
  },
  badgeSlot: {
    height: 28,
    marginBottom: spacing.sm,
    justifyContent: 'center',
  },
  planName: {
    fontSize: Platform.select({ web: 20, default: 18 }),
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  price: {
    flexShrink: 1,
    fontSize: PRICE_BASE_FONT_SIZE,
    lineHeight: Platform.select({ web: 44, default: 36 }),
    color: colors.text.primary,
  },
  // Loading / unavailable copy is prose, not a number, so it takes body sizing
  // rather than the metric scale.
  priceFallback: {
    fontSize: Platform.select({ web: 20, default: 18 }),
    letterSpacing: -0.3,
  },
  // Fixed slot so the caption's presence or absence (loading, unavailable)
  // never shifts the CTA row out of alignment across cards.
  priceCaptionSlot: {
    height: 16,
    justifyContent: 'flex-end',
  },
  priceCurrency: {
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  billedYearlySlot: {
    height: 18,
    marginBottom: spacing.md,
    justifyContent: 'center',
  },
  description: {
    lineHeight: Platform.select({ web: 20, default: 20 }),
    // Reserve a uniform ~4-line block on web so cards with shorter copy don't
    // pull their CTA button upward — every "Start Checkout" stays aligned.
    minHeight: Platform.select({ web: 84, default: undefined }),
  },
  ctaButton: {
    marginBottom: spacing.lg,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.lg,
  },
  featureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
    paddingVertical: Platform.select({ web: 6, default: spacing.sm }),
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border.subtle,
  },
  featureLabel: {
    flex: 1,
    minWidth: 0,
  },
});

const sectionStyles = StyleSheet.create({
  section: {
    backgroundColor: colors.transparent,
    paddingTop: Platform.select({ web: 96, default: 64 }),
    paddingBottom: Platform.select({ web: 96, default: 64 }),
  },
  header: {
    alignItems: 'center',
    marginBottom: Platform.select({ web: 32, default: 24 }),
  },
  badge: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.primary.muted,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: Platform.select({ web: 44, default: 30 }),
    lineHeight: Platform.select({ web: 50, default: 36 }),
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: spacing.md,
    maxWidth: 800,
    color: colors.text.primary,
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 560,
  },
  toggleContainer: {
    alignItems: 'center',
    marginBottom: Platform.select({ web: 44, default: 32 }),
    gap: spacing.md,
  },
  creditsDisclaimer: {
    textAlign: 'center',
    maxWidth: 560,
    paddingHorizontal: spacing.lg,
    lineHeight: 18,
  },
  grid: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'stretch',
    gap: Platform.select({ web: 16, default: 12 }),
  },
  scrollContainer: {
    flexDirection: 'row',
    gap: 20,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
});
